// Profiles review: facts about people, groups, companies and brands that the loaders
// were not sure enough of to publish, and profiles that may be one person or company.
// Served by ff-api at /api/admin/v2/profiles (read on cms.view, decide on cms.publish).
// Nothing here is public yet.
import React, { useEffect, useMemo, useState } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  GitMerge,
  Loader2,
  PauseCircle,
  RefreshCw,
  RotateCcw,
  Search,
  Split,
  User,
  Users,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  profileKeys,
  useClaimReviewMutation,
  useDuplicateDecisionMutation,
  useDuplicatesQuery,
  useProfileQuery,
  useProfileQueueQuery,
  useProfileStatsQuery,
} from '@/client/api/profiles/profiles.query';
import {
  Claim,
  ClaimDecision,
  ClaimStatus,
  DuplicatePair,
  PairStatus,
  ProfileRef,
} from '@/client/types/profiles';
import { PageShell, Pagination, StatCard, fmtNum } from '../Integrations/integrationsUi';
import { Card, EmptyRow, Field, INPUT_CLASS, Th } from '../Scheduler/schedulerUi';
import { DeleteConfirmDialog, RowMenu, RowMenuItem } from '../HumanResource/components/hr-ui';

const BASE = '/admin/content/profiles';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'held', label: 'Held (weak evidence)' },
  { value: 'needs_review,held', label: 'Needs review + held' },
  { value: 'published,approved', label: 'Live' },
  { value: 'rejected', label: 'Rejected' },
];

const SOURCE_LABEL: Record<string, string> = {
  wikidata: 'Wikidata',
  wikipedia: 'Wikipedia articles',
  news: 'News headlines',
};

const KIND_LABEL: Record<string, string> = {
  person: 'Person',
  group: 'Group',
  company: 'Company',
  brand: 'Brand',
};

const STATUS_STYLE: Record<ClaimStatus, string> = {
  needs_review: 'bg-amber-50 text-amber-700 ring-amber-200',
  held: 'bg-slate-100 text-slate-600 ring-slate-200',
  published: 'bg-green-50 text-green-700 ring-green-200',
  approved: 'bg-green-50 text-green-700 ring-green-200',
  rejected: 'bg-red-50 text-red-700 ring-red-200',
  duplicate: 'bg-slate-100 text-slate-500 ring-slate-200',
  withdrawn: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const PAIR_STYLE: Record<PairStatus, string> = {
  likely: 'bg-amber-50 text-amber-700 ring-amber-200',
  possible: 'bg-blue-50 text-blue-700 ring-blue-200',
  merged: 'bg-green-50 text-green-700 ring-green-200',
  distinct: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const SOURCE_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#9333ea', '#0891b2', '#dc2626'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A date printed only as exactly as the source gave it. */
const fmtClaimDate = (c: Pick<Claim, 'year' | 'month' | 'day' | 'year_end' | 'date_precision'>) => {
  if (c.year == null) return 'No date';
  const year = c.year < 0 ? `${-c.year} BCE` : String(c.year);
  const range = c.year_end && c.year_end !== c.year ? `–${c.year_end}` : '';
  switch (c.date_precision) {
    case 'day':
      return `${c.day} ${MONTHS[(c.month ?? 1) - 1]} ${year}`;
    case 'month':
      return `${MONTHS[(c.month ?? 1) - 1]} ${year}`;
    case 'circa':
      return `c. ${year}`;
    case 'decade':
      return `${year}s`;
    default:
      return `${year}${range}`;
  }
};

const fmtWhen = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '');

const errorText = (error: unknown) =>
  error instanceof Error && error.message ? error.message : 'Something went wrong reading profiles.';

const Pill: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

const StatusBadge: React.FC<{ status: ClaimStatus }> = ({ status }) => (
  <Pill className={STATUS_STYLE[status] ?? STATUS_STYLE.held}>{status.replace('_', ' ')}</Pill>
);

const ErrorBox: React.FC<{ error: unknown; onRetry?: () => void }> = ({ error, onRetry }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
    <span>{errorText(error)}</span>
    {onRetry && (
      <Button size="sm" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

const LoadingNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    role="status"
    className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800"
  >
    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
    <span>{children}</span>
  </div>
);

const FetchingChip: React.FC = () =>
  useIsFetching({ queryKey: profileKeys.all }) > 0 ? (
    <span
      role="status"
      className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
    >
      <Loader2 className="h-3 w-3 animate-spin" />
      Updating
    </span>
  ) : null;

/** A sortable header: one click picks the column, the arrow says it is the order in use. */
const SortTh: React.FC<{ active: boolean; onClick: () => void; right?: boolean; children: React.ReactNode }> = ({
  active,
  onClick,
  right,
  children,
}) => (
  <th className={`px-4 py-2 font-medium ${right ? 'text-right' : ''}`}>
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
    >
      {children}
      {active && <ArrowDown className="h-3 w-3" />}
    </button>
  </th>
);

/* ---------- trends ---------- */

const days = (n: number) => {
  const out: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

const dayLabel = (iso: string) => {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}`;
};

const DailyBars: React.FC<{
  rows: { day: string; key: string; count: number }[];
  keys: string[];
  label: (k: string) => string;
  color: (k: string, i: number) => string;
  span: number;
  empty: string;
}> = ({ rows, keys, label, color, span, empty }) => {
  const data = days(span).map((day) => {
    const point: Record<string, string | number> = { day: dayLabel(day) };
    keys.forEach((k) => {
      point[label(k)] = 0;
    });
    rows
      .filter((r) => r.day === day)
      .forEach((r) => {
        point[label(r.key)] = Number(point[label(r.key)] ?? 0) + r.count;
      });
    return point;
  });
  if (!rows.length) return <p className="py-10 text-center text-sm text-gray-400">{empty}</p>;
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="15%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
          />
          <YAxis
            allowDecimals={false}
            width={40}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
          />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.03)' }}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {keys.map((k, i) => (
            <Bar key={k} dataKey={label(k)} stackId="s" fill={color(k, i)} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ---------- one fact, with its profile ---------- */

const ProfileLine: React.FC<{ profile?: ProfileRef | null; onOpen?: (id: number) => void }> = ({
  profile,
  onOpen,
}) =>
  profile ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(profile.id);
      }}
      className="text-left font-medium text-gray-900 hover:text-blue-700 hover:underline"
    >
      {profile.name}
    </button>
  ) : (
    <span className="text-gray-500">Unknown profile</span>
  );

const Sources: React.FC<{ claim: Claim }> = ({ claim }) => (
  <ul className="mt-1 space-y-3">
    {claim.sources.length === 0 && <li className="text-gray-500">No source recorded.</li>}
    {claim.sources.map((s, i) => (
      <li key={`${s.url}-${i}`} className="rounded-lg border border-gray-100 p-3">
        {s.quote && <p className="mb-2 italic text-gray-700">&ldquo;{s.quote}&rdquo;</p>}
        {s.url ? (
          <a
            href={s.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 break-all text-blue-600 hover:underline"
          >
            {s.title || s.publisher || s.url}
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        ) : (
          <span className="text-gray-800">{s.title || s.publisher || s.kind}</span>
        )}
        <p className="mt-1 text-xs text-gray-500">
          {[s.publisher, s.license, s.published_at && fmtWhen(s.published_at)].filter(Boolean).join(' · ')}
        </p>
      </li>
    ))}
  </ul>
);

const ClaimSheet: React.FC<{
  claim: Claim | null;
  canDecide: boolean;
  onDecide: (claim: Claim, status: ClaimDecision) => void;
  onOpenProfile: (id: number) => void;
  onClose: () => void;
}> = ({ claim, canDecide, onDecide, onOpenProfile, onClose }) => (
  <Sheet open={!!claim} onOpenChange={(open) => !open && onClose()}>
    <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
      <SheetHeader>
        <SheetTitle>{claim?.profile?.name ?? 'Fact'}</SheetTitle>
        <SheetDescription>
          {claim ? `${claim.predicate.replace(/_/g, ' ')} · ${fmtClaimDate(claim)}` : ''}
        </SheetDescription>
      </SheetHeader>
      {claim && (
        <div className="mt-4 space-y-5 text-sm">
          <p className="text-base text-gray-900">{claim.statement}</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <StatusBadge status={claim.review_status} />
            </Field>
            <Field label="Source">{SOURCE_LABEL[claim.origin ?? ''] ?? claim.origin}</Field>
            <Field label="Score">
              {claim.score.toFixed(2)}
              {claim.score_parts && (
                <span className="ml-2 text-xs text-gray-500">
                  source {claim.score_parts.source} × date {claim.score_parts.date} × match{' '}
                  {claim.score_parts.match}
                </span>
              )}
            </Field>
            <Field label="Profile">
              <ProfileLine profile={claim.profile} onOpen={onOpenProfile} />
              <span className="ml-2 text-xs text-gray-500">
                {KIND_LABEL[claim.profile?.kind ?? ''] ?? ''}
                {claim.profile?.living === false ? ' · deceased' : ''}
              </span>
            </Field>
            {claim.object && (
              <Field label="About">
                {claim.object.profile ? (
                  <ProfileLine profile={claim.object.profile} onOpen={onOpenProfile} />
                ) : (
                  claim.object.label ?? claim.object.ref
                )}
              </Field>
            )}
            {claim.reviewer && (
              <Field label="Decided by">
                {claim.reviewer} {claim.reviewed_at ? `on ${fmtWhen(claim.reviewed_at)}` : ''}
              </Field>
            )}
          </div>
          {claim.profile?.living !== false && claim.profile?.kind === 'person' && (
            <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              This person may be alive. Their facts are only shown once a person has checked them.
            </p>
          )}
          <div>
            <p className="text-xs font-medium text-gray-500">Sources</p>
            <Sources claim={claim} />
          </div>
          {canDecide && (
            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <Button size="sm" onClick={() => onDecide(claim, 'approved')}>
                <Check className="mr-2 h-3.5 w-3.5" />
                Approve
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDecide(claim, 'rejected')}>
                <X className="mr-2 h-3.5 w-3.5" />
                Reject
              </Button>
              {claim.review_status !== 'needs_review' && (
                <Button size="sm" variant="outline" onClick={() => onDecide(claim, 'needs_review')}>
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  Back to review
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </SheetContent>
  </Sheet>
);

/* ---------- a whole profile ---------- */

const FactList: React.FC<{ title: string; claims: Claim[]; from?: boolean; onOpen: (id: number) => void }> = ({
  title,
  claims,
  from,
  onOpen,
}) =>
  claims.length ? (
    <div>
      <p className="text-xs font-medium text-gray-500">
        {title} ({claims.length})
      </p>
      <ul className="mt-1 divide-y divide-gray-50">
        {claims.slice(0, 40).map((c) => (
          <li key={`${title}-${c.id}`} className="flex items-start justify-between gap-3 py-1.5">
            <div className="min-w-0">
              <p className="text-gray-800">{c.statement}</p>
              <p className="text-xs text-gray-500">
                {fmtClaimDate(c)}
                {from && c.from && (
                  <>
                    {' · from '}
                    <ProfileLine profile={c.from} onOpen={onOpen} />
                  </>
                )}
                {!from && c.object?.profile && (
                  <>
                    {' · '}
                    <ProfileLine profile={c.object.profile} onOpen={onOpen} />
                  </>
                )}
                {c.object?.listing && ` · FF listing #${c.object.listing.eid}`}
              </p>
            </div>
            <StatusBadge status={c.review_status} />
          </li>
        ))}
      </ul>
      {claims.length > 40 && <p className="mt-1 text-xs text-gray-400">and {claims.length - 40} more</p>}
    </div>
  ) : null;

const ProfileSheet: React.FC<{ id: number | null; onOpen: (id: number) => void; onClose: () => void }> = ({
  id,
  onOpen,
  onClose,
}) => {
  const q = useProfileQuery(id);
  const p = q.data;
  return (
    <Sheet open={id != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{p?.name ?? 'Profile'}</SheetTitle>
          <SheetDescription>
            {p
              ? [KIND_LABEL[p.kind], p.qid, p.living === false ? 'deceased' : p.living ? 'living' : null]
                  .filter(Boolean)
                  .join(' · ')
              : ''}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-5 text-sm">
          {q.isLoading && <LoadingNote>Reading this profile and every fact about it.</LoadingNote>}
          {q.isError && <ErrorBox error={q.error} onRetry={() => q.refetch()} />}
          {p && (
            <>
              {p.qid && (
                <a
                  href={`https://www.wikidata.org/wiki/${p.qid}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  Wikidata {p.qid}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {p.names.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Names</p>
                  <p className="mt-1 text-gray-800">
                    {p.names
                      .map((n) => (n.from || n.to ? `${n.name} (${n.from ?? ''}–${n.to ?? ''})` : n.name))
                      .join(' · ')}
                  </p>
                </div>
              )}
              {p.media.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {p.media.slice(0, 6).map((m) => (
                    <a key={m.id} href={m.source_page ?? m.url} target="_blank" rel="noreferrer">
                      <img
                        src={m.thumb_url ?? m.url}
                        alt={m.caption ?? ''}
                        className="h-24 rounded-lg border border-gray-100 object-cover"
                      />
                    </a>
                  ))}
                </div>
              )}
              <FactList title="Timeline" claims={p.timeline} onOpen={onOpen} />
              <FactList title="Connections" claims={p.connections} onOpen={onOpen} />
              <FactList title="Members" claims={p.members} from onOpen={onOpen} />
              <FactList title="Other facts" claims={p.facts} onOpen={onOpen} />
              <FactList title="Mentioned by" claims={p.mentioned_in} from onOpen={onOpen} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ---------- confirmation ---------- */

interface Pending {
  title: string;
  description: string;
  action: string;
  danger: boolean;
  run: () => void;
}

const DECISION_WORDS: Record<ClaimDecision, { verb: string; action: string; danger: boolean }> = {
  approved: { verb: 'Approve', action: 'Approve', danger: false },
  rejected: { verb: 'Reject', action: 'Reject', danger: true },
  needs_review: { verb: 'Send back to review', action: 'Send back', danger: false },
};

/* ---------- facts tab ---------- */

const FactsTab: React.FC<{
  canView: boolean;
  canDecide: boolean;
  sources: string[];
  confirm: (p: Pending) => void;
  onOpenProfile: (id: number) => void;
}> = ({ canView, canDecide, sources, confirm, onOpenProfile }) => {
  const [status, setStatus] = useState('needs_review');
  const [origin, setOrigin] = useState('');
  const [kind, setKind] = useState('');
  const [sort, setSort] = useState<'score' | 'newest'>('score');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState<Claim | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [status, origin, kind, sort, debounced, perPage]);

  const listQ = useProfileQueueQuery(
    { status, origin, kind, sort, q: debounced, limit: perPage, offset: (page - 1) * perPage },
    canView,
  );
  const review = useClaimReviewMutation();
  const rows = listQ.data?.claims ?? [];
  const total = listQ.data?.total ?? 0;

  const decide = (claims: Claim[], decision: ClaimDecision) => {
    const w = DECISION_WORDS[decision];
    const one = claims.length === 1 ? claims[0] : null;
    confirm({
      title: one ? `${w.verb} this fact?` : `${w.verb} ${claims.length} facts?`,
      description: one
        ? `"${one.statement}" on ${one.profile?.name ?? 'this profile'}.${
            decision === 'approved' ? ' It will show on the profile.' : ''
          }`
        : `The ${claims.length} selected facts.${decision === 'approved' ? ' They will show on their profiles.' : ''}`,
      action: w.action,
      danger: w.danger,
      run: () =>
        review.mutate(
          { ids: claims.map((c) => c.id), status: decision },
          {
            onSuccess: () => {
              setSelected(new Set());
              setOpen(null);
            },
          },
        ),
    });
  };

  const menu = (c: Claim): RowMenuItem[] => [
    { label: 'View fact', icon: Search, onSelect: () => setOpen(c) },
    { label: 'Open profile', icon: User, onSelect: () => c.profile && onOpenProfile(c.profile.id) },
    {
      label: 'Open source',
      icon: ExternalLink,
      onSelect: () => window.open(c.sources[0]?.url ?? '', '_blank', 'noreferrer'),
      hidden: !c.sources[0]?.url,
    },
    {
      label: 'Approve',
      icon: Check,
      onSelect: () => decide([c], 'approved'),
      hidden: !canDecide || c.review_status === 'approved',
    },
    {
      label: 'Reject',
      icon: X,
      onSelect: () => decide([c], 'rejected'),
      danger: true,
      hidden: !canDecide || c.review_status === 'rejected',
    },
    {
      label: 'Back to review',
      icon: RotateCcw,
      onSelect: () => decide([c], 'needs_review'),
      hidden: !canDecide || c.review_status === 'needs_review',
    },
  ];

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allTicked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const ticked = rows.filter((r) => selected.has(r.id));
  const cols = canDecide ? 9 : 8;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a fact or a name"
            className={`${INPUT_CLASS} w-64 pl-8`}
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={INPUT_CLASS}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select value={origin} onChange={(e) => setOrigin(e.target.value)} className={INPUT_CLASS}>
          <option value="">Any source</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {SOURCE_LABEL[s] ?? s}
            </option>
          ))}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={INPUT_CLASS}>
          <option value="">Any kind</option>
          {Object.entries(KIND_LABEL).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        {canDecide && ticked.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">{ticked.length} selected</span>
            <Button size="sm" disabled={review.isPending} onClick={() => decide(ticked, 'approved')}>
              <Check className="mr-2 h-3.5 w-3.5" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={review.isPending}
              onClick={() => decide(ticked, 'rejected')}
            >
              <X className="mr-2 h-3.5 w-3.5" />
              Reject
            </Button>
          </div>
        )}
      </div>

      {listQ.isError && <ErrorBox error={listQ.error} onRetry={() => listQ.refetch()} />}
      {review.isError && <ErrorBox error={review.error} />}

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                {canDecide && (
                  <Th>
                    <input
                      type="checkbox"
                      aria-label="Select every row on this page"
                      checked={allTicked}
                      onChange={() => setSelected(allTicked ? new Set() : new Set(rows.map((r) => r.id)))}
                    />
                  </Th>
                )}
                <Th>Profile</Th>
                <Th>Fact</Th>
                <Th>When</Th>
                <Th>Source</Th>
                <SortTh right active={sort === 'score'} onClick={() => setSort('score')}>
                  Score
                </SortTh>
                <SortTh active={sort === 'newest'} onClick={() => setSort('newest')}>
                  Added
                </SortTh>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <EmptyRow cols={cols}>
                  <span className="inline-flex items-center gap-2 text-blue-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Reading facts from the people database
                  </span>
                </EmptyRow>
              )}
              {!listQ.isLoading && !listQ.isError && rows.length === 0 && (
                <EmptyRow cols={cols}>Nothing matches these filters</EmptyRow>
              )}
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50/70"
                  onClick={() => setOpen(c)}
                >
                  {canDecide && (
                    <td className="px-4 py-2" onClick={(ev) => ev.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${c.statement}`}
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                      />
                    </td>
                  )}
                  <td className="whitespace-nowrap px-4 py-2">
                    <ProfileLine profile={c.profile} onOpen={onOpenProfile} />
                    <p className="text-xs text-gray-500">{KIND_LABEL[c.profile?.kind ?? ''] ?? ''}</p>
                  </td>
                  <td className="max-w-md px-4 py-2">
                    <p className="text-gray-900">{c.statement}</p>
                    <p className="text-xs text-gray-500">{c.predicate.replace(/_/g, ' ')}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700">{fmtClaimDate(c)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                    {SOURCE_LABEL[c.origin ?? ''] ?? c.origin}
                    {c.sources[0]?.publisher && (
                      <p className="text-xs text-gray-500">{c.sources[0].publisher}</p>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{c.score.toFixed(2)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700">{fmtWhen(c.created_at)}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={c.review_status} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <RowMenu items={menu(c)} label={`Actions for ${c.statement}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pages={Math.max(1, Math.ceil(total / perPage))}
          total={total}
          perPage={perPage}
          onPage={setPage}
          onPerPage={setPerPage}
        />
      </div>

      <ClaimSheet
        claim={open}
        canDecide={canDecide}
        onDecide={(c, d) => decide([c], d)}
        onOpenProfile={onOpenProfile}
        onClose={() => setOpen(null)}
      />
    </>
  );
};

/* ---------- duplicates tab ---------- */

const PairSide: React.FC<{ side: DuplicatePair['a']; onOpen: (id: number) => void }> = ({ side, onOpen }) => (
  <div>
    <ProfileLine profile={side} onOpen={onOpen} />
    <p className="text-xs text-gray-500">
      {[side.qid, `${side.claims} facts`, side.living === false ? 'deceased' : null].filter(Boolean).join(' · ')}
    </p>
  </div>
);

const DuplicatesTab: React.FC<{
  canView: boolean;
  canDecide: boolean;
  counts: Partial<Record<PairStatus, number>>;
  confirm: (p: Pending) => void;
  onOpenProfile: (id: number) => void;
}> = ({ canView, canDecide, counts, confirm, onOpenProfile }) => {
  const [status, setStatus] = useState<PairStatus | ''>('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  useEffect(() => setPage(1), [status, perPage]);
  const listQ = useDuplicatesQuery(status, (page - 1) * perPage, perPage, canView);
  const decideM = useDuplicateDecisionMutation();
  const rows = listQ.data ?? [];
  const total = status ? counts[status] ?? 0 : (counts.likely ?? 0) + (counts.possible ?? 0);
  const open = (status === '' || status === 'likely' || status === 'possible');

  const merge = (d: DuplicatePair, keep: DuplicatePair['a'], drop: DuplicatePair['a']) =>
    confirm({
      title: `Merge into ${keep.name}?`,
      description: `${drop.name}'s names, ${drop.claims} facts and photos move to ${keep.name} (${
        keep.qid ?? `#${keep.id}`
      }). ${drop.name} stops being a separate profile. Reasons given: ${(d.basis ?? []).join('; ') || 'none'}.`,
      action: 'Merge',
      danger: true,
      run: () => decideM.mutate({ id: d.id, decision: 'same', keep: keep.id }),
    });
  const apart = (d: DuplicatePair) =>
    confirm({
      title: 'Keep them as two profiles?',
      description: `${d.a.name} and ${d.b.name} will not be suggested as a pair again.`,
      action: 'Not the same',
      danger: false,
      run: () => decideM.mutate({ id: d.id, decision: 'distinct' }),
    });

  const menu = (d: DuplicatePair): RowMenuItem[] => [
    { label: `View A: ${d.a.name}`, icon: User, onSelect: () => onOpenProfile(d.a.id) },
    { label: `View B: ${d.b.name}`, icon: Users, onSelect: () => onOpenProfile(d.b.id) },
    {
      label: `Merge, keep A (${d.a.qid ?? d.a.id})`,
      icon: GitMerge,
      onSelect: () => merge(d, d.a, d.b),
      hidden: !canDecide || !open,
    },
    {
      label: `Merge, keep B (${d.b.qid ?? d.b.id})`,
      icon: GitMerge,
      onSelect: () => merge(d, d.b, d.a),
      hidden: !canDecide || !open,
    },
    { label: 'Not the same', icon: Split, onSelect: () => apart(d), hidden: !canDecide || !open },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as PairStatus | '')}
          className={INPUT_CLASS}
        >
          <option value="">Waiting (likely + possible)</option>
          <option value="likely">Likely</option>
          <option value="possible">Possible</option>
          <option value="merged">Merged</option>
          <option value="distinct">Not the same</option>
        </select>
        <p className="text-xs text-gray-500">
          A pair needs more than a shared name: a birth or founding year, a website or a Wikidata link.
        </p>
      </div>
      {listQ.isError && <ErrorBox error={listQ.error} onRetry={() => listQ.refetch()} />}
      {decideM.isError && <ErrorBox error={decideM.error} />}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th right>Score</Th>
                <Th>Profile A</Th>
                <Th>Profile B</Th>
                <Th>Why they may be one</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <EmptyRow cols={6}>
                  <span className="inline-flex items-center gap-2 text-blue-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Reading possible duplicates
                  </span>
                </EmptyRow>
              )}
              {!listQ.isLoading && !listQ.isError && rows.length === 0 && (
                <EmptyRow cols={6}>No pairs in this list</EmptyRow>
              )}
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className="cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50/70"
                  onClick={() => onOpenProfile(d.a.id)}
                >
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{d.score.toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <PairSide side={d.a} onOpen={onOpenProfile} />
                  </td>
                  <td className="px-4 py-2">
                    <PairSide side={d.b} onOpen={onOpenProfile} />
                  </td>
                  <td className="max-w-md px-4 py-2 text-xs text-gray-600">{(d.basis ?? []).join(' · ')}</td>
                  <td className="px-4 py-2">
                    <Pill className={PAIR_STYLE[d.status]}>{d.status === 'distinct' ? 'not the same' : d.status}</Pill>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <RowMenu items={menu(d)} label={`Actions for ${d.a.name} and ${d.b.name}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pages={Math.max(1, Math.ceil(total / perPage))}
          total={total}
          perPage={perPage}
          onPage={setPage}
          onPerPage={setPerPage}
        />
      </div>
    </>
  );
};

/* ---------- page ---------- */

type TabKey = 'facts' | 'duplicates';

const ProfilesReviewPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Content', href: '/admin/content/pages' },
    { label: 'Profiles', href: BASE },
  ]);
  const queryClient = useQueryClient();
  const viewPerm = useHasPermission('cms.view');
  const decidePerm = useHasPermission('cms.publish');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canDecide = decidePerm.isSuperAdmin || decidePerm.hasPermission;

  const [tab, setTab] = useState<TabKey>('facts');
  const [span, setSpan] = useState(30);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const statsQ = useProfileStatsQuery(span, canView);
  const st = statsQ.data;

  const counts = useMemo(() => {
    const by: Record<string, number> = {};
    for (const r of st?.claims ?? []) by[r.status] = (by[r.status] ?? 0) + r.count;
    return by;
  }, [st]);
  const sources = useMemo(() => Array.from(new Set((st?.claims ?? []).map((r) => r.origin))).sort(), [st]);
  const addedSources = useMemo(() => Array.from(new Set((st?.added ?? []).map((r) => r.origin))).sort(), [st]);
  const profilesTotal = Object.values(st?.profiles ?? {}).reduce((a, b) => a + (b ?? 0), 0);
  const pairsWaiting = (st?.duplicates.likely ?? 0) + (st?.duplicates.possible ?? 0);

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Profiles" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Profiles" description="">
        <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
          <p className="text-sm font-medium text-gray-800">You don&apos;t have access to profiles</p>
          <p className="mt-1 text-sm text-gray-500">Ask an administrator for the cms.view permission.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Profiles"
      description="Facts about Ghanaian people, groups, companies and brands, each with its source, waiting for someone to check them. Nothing here is public yet."
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          <select
            value={span}
            onChange={(e) => setSpan(Number(e.target.value))}
            className={INPUT_CLASS}
            aria-label="Trend window"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: profileKeys.all })}
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      }
    >
      {statsQ.isLoading && <LoadingNote>Counting facts, profiles and duplicate pairs.</LoadingNote>}
      {statsQ.isError && <ErrorBox error={statsQ.error} onRetry={() => statsQ.refetch()} />}

      {st && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <StatCard
              label="Needs review"
              value={fmtNum(counts.needs_review ?? 0)}
              icon={<Clock className="h-5 w-5 text-amber-600" />}
              color="bg-amber-50"
            />
            <StatCard
              label="Held, weak evidence"
              value={fmtNum(counts.held ?? 0)}
              icon={<PauseCircle className="h-5 w-5 text-slate-600" />}
              color="bg-slate-100"
            />
            <StatCard
              label="Live facts"
              value={fmtNum((counts.published ?? 0) + (counts.approved ?? 0))}
              hint={`${fmtNum(counts.rejected ?? 0)} rejected`}
              icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
              color="bg-green-50"
            />
            <StatCard
              label="Possible duplicates"
              value={fmtNum(pairsWaiting)}
              hint={`${fmtNum(st.duplicates.merged ?? 0)} merged`}
              icon={<GitMerge className="h-5 w-5 text-blue-600" />}
              color="bg-blue-50"
            />
            <StatCard
              label="Profiles"
              value={fmtNum(profilesTotal)}
              hint={(['person', 'group', 'company', 'brand'] as const)
                .map((k) => `${fmtNum(st.profiles[k] ?? 0)} ${k === 'company' ? 'companies' : `${k}s`}`)
                .join(' · ')}
              icon={<Users className="h-5 w-5 text-purple-600" />}
              color="bg-purple-50"
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Facts added per day" note="by source">
              <DailyBars
                rows={st.added.map((r) => ({ day: r.day, key: r.origin, count: r.count }))}
                keys={addedSources}
                label={(k) => SOURCE_LABEL[k] ?? k}
                color={(_k, i) => SOURCE_COLORS[i % SOURCE_COLORS.length]}
                span={span}
                empty="No facts added in this window."
              />
            </Card>
            <Card title="Decisions per day" note="by people, not the scorer">
              <DailyBars
                rows={st.decided.map((r) => ({ day: r.day, key: r.status, count: r.count }))}
                keys={['approved', 'rejected', 'needs_review']}
                label={(k) => (k === 'needs_review' ? 'Sent back' : k[0].toUpperCase() + k.slice(1))}
                color={(k) => (k === 'approved' ? '#16a34a' : k === 'rejected' ? '#dc2626' : '#f59e0b')}
                span={span}
                empty="Nobody has decided anything in this window yet."
              />
            </Card>
          </div>
        </>
      )}

      <div className="flex items-center gap-1 border-b border-gray-200">
        {(
          [
            ['facts', `Facts to check (${fmtNum(counts.needs_review ?? 0)})`],
            ['duplicates', `Possible duplicates (${fmtNum(pairsWaiting)})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'facts' ? (
        <FactsTab
          canView={canView}
          canDecide={canDecide}
          sources={sources}
          confirm={setPending}
          onOpenProfile={setProfileId}
        />
      ) : (
        <DuplicatesTab
          canView={canView}
          canDecide={canDecide}
          counts={st?.duplicates ?? {}}
          confirm={setPending}
          onOpenProfile={setProfileId}
        />
      )}

      <ProfileSheet id={profileId} onOpen={setProfileId} onClose={() => setProfileId(null)} />
      <DeleteConfirmDialog
        open={!!pending}
        title={pending?.title ?? ''}
        description={pending?.description ?? ''}
        actionLabel={pending?.action ?? 'Confirm'}
        danger={pending?.danger ?? false}
        onOpenChange={(o) => !o && setPending(null)}
        onConfirm={() => {
          pending?.run();
          setPending(null);
        }}
      />
    </PageShell>
  );
};

export default ProfilesReviewPage;
